import React, { useEffect, useRef } from 'react';
import { SH } from './Section';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion } from 'framer-motion';
import { StarIcon, QuoteIcon } from './BrandIcons';

const LinkedInIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TopmateIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
  </svg>
);

const StackedAvatars = () => {
  const avatarColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
  return (
    <div style={{ display: 'flex', marginRight: '12px' }}>
      {avatarColors.map((color, i) => (
        <div
          key={i}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${color}, ${color}99)`,
            border: '2px solid rgba(255,255,255,0.2)',
            marginLeft: i === 0 ? 0 : '-10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            color: '#fff',
            zIndex: 5 - i
          }}
        >
          {String.fromCharCode(65 + i)}
        </div>
      ))}
    </div>
  );
};

const StarRating = ({ rating = 4.9 }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon key={star} size={16} filled={star <= rating} gradient={star <= rating} />
      ))}
      <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 600, color: theme === 'dark' ? '#fff' : '#1a1a2e' }}>
        {rating}/5
      </span>
    </div>
  );
};

const TestimonialCard = ({ quote, name, role }) => {
  const { theme } = useTheme();
  return (
    <div
      style={{
        minWidth: '340px',
        maxWidth: '340px',
        padding: '24px',
        borderRadius: '16px',
        background: theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: theme === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        cursor: 'default',
        flexShrink: 0
      }}
    >
      <QuoteIcon size={24} gradient style={{ marginBottom: '16px', opacity: 0.7 }} />
      <p
        className="font-[Manrope]"
        style={{
          fontSize: '14px',
          lineHeight: 1.7,
          marginBottom: '20px',
          fontStyle: 'italic',
          color: theme === 'dark' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)',
          minHeight: '72px'
        }}
      >
        "{quote}"
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.secondary}, ${C.accent})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
            color: '#fff'
          }}
        >
          {name.charAt(0)}
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: theme === 'dark' ? '#fff' : '#1a1a2e' }}>{name}</div>
          <div style={{ fontSize: '12px', color: C.secondary }}>{role}</div>
        </div>
      </div>
    </div>
  );
};

const ScrollingRow = ({ testimonials, direction = 'left' }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = direction === 'right' ? scrollContainer.scrollWidth / 2 : 0;

    const animate = () => {
      if (direction === 'left') {
        scrollPosition += 0.5;
        if (scrollPosition >= scrollContainer.scrollWidth / 2) {
          scrollPosition = 0;
        }
      } else {
        scrollPosition -= 0.5;
        if (scrollPosition <= 0) {
          scrollPosition = scrollContainer.scrollWidth / 2;
        }
      }
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => { if (animationId) cancelAnimationFrame(animationId); };
  }, [direction]);

  const duplicated = [...testimonials, ...testimonials];

  return (
    <div
      ref={scrollRef}
      style={{
        display: 'flex',
        gap: '20px',
        overflow: 'hidden',
        padding: '10px 0',
        maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)'
      }}
    >
      {duplicated.map((t, i) => (
        <TestimonialCard key={i} quote={t.quote} name={t.name} role={t.role} />
      ))}
    </div>
  );
};

export const TestimonialsSection = ({
  eyebrow = "TESTIMONIALS",
  title = "What Users",
  accent = "Say",
  testimonials,
  showHeader = true
}) => {
  const { theme } = useTheme();

  const defaultTestimonials = [
    { quote: "Very detailed and well researched. Highlighted specific missing pieces and areas of improvement. Highly recommend.", name: "MB", role: "Founder" },
    { quote: "The DecX Score was eye-opening. I was burying my traction on slide 9. Fixed it. Closed my round in 3 weeks.", name: "Alex K", role: "Seed Stage" },
    { quote: "Close rate went from 12% to 28% in one quarter. The outreach sequences alone were worth it.", name: "Sarah M", role: "Sales Lead" },
    { quote: "Finally understood why leadership kept asking for more data. Shortened approval cycle by 50%.", name: "David L", role: "Product Lead" },
    { quote: "The battle cards were game-changing. We now win 3x more competitive deals.", name: "Mike R", role: "VP Sales" },
    { quote: "Got 12 investor meetings in 2 weeks using the outreach sequences. Incredible ROI.", name: "Priya S", role: "Founder" }
  ];

  const allTestimonials = testimonials || defaultTestimonials;

  return (
    <div style={{ marginTop: showHeader ? '80px' : '40px' }}>
      {showHeader && (
        <SH eyebrow={eyebrow} title={title} accent={accent} center />
      )}

      {/* Rating & Social Proof */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          marginTop: '32px',
          marginBottom: '40px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
          <StackedAvatars />
          <StarRating rating={4.9} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '12px', fontWeight: 500, letterSpacing: '0.05em', color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)' }}>
            Verified on
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#0A66C2', padding: '6px 12px', borderRadius: '6px', background: 'rgba(10, 102, 194, 0.1)' }}>
              <LinkedInIcon size={18} />
              <span style={{ fontSize: '12px', fontWeight: 600 }}>LinkedIn</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#FF6B6B', padding: '6px 12px', borderRadius: '6px', background: 'rgba(255, 107, 107, 0.1)' }}>
              <TopmateIcon size={18} />
              <span style={{ fontSize: '12px', fontWeight: 600 }}>Topmate</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scrolling rows - both use full allTestimonials */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'hidden' }}>
        <ScrollingRow testimonials={allTestimonials} direction="left" />
        <ScrollingRow testimonials={allTestimonials} direction="right" />
      </div>
    </div>
  );
};

export default TestimonialsSection;